import { useState, useMemo, useEffect, useRef } from "react";
import api, { resolveImageUrl } from "../lib/api";
import { toast } from "sonner";
import {
  Phone, MessageCircle, MapPin, Wallet, KeyRound, Loader2, CheckCircle,
  Star, XCircle, Zap, Navigation, Map as MapIcon, Share2, Flag
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import LiveTrackingMap from "./LiveTrackingMap";
import ReviewModal from "./ReviewModal";
import ReviewBadge from "./ReviewBadge";
import TripChronometer from "./TripChronometer";
import DriverPickupMapDialog from "./driver/DriverPickupMapDialog";
import CancelBookingDialog from "./CancelBookingDialog";

function fmtXAF(n) { return `${Number(n || 0).toLocaleString("fr-FR").replace(/,/g, " ")} F`; }

const STATUS_LABEL = {
  pending:     { label: "En attente d'acceptation", cls: "bg-[#FEF3C7] text-[#92400E]" },
  confirmed:   { label: "Chauffeur en route",       cls: "bg-[#DBEAFE] text-[#1D4ED8]" },
  in_progress: { label: "Course en cours",          cls: "bg-[#D1FAE5] text-[#065F46]" },
  completed:   { label: "Course terminée",          cls: "bg-[#F1F5F9] text-[#0A2540]" },
  cancelled:   { label: "Annulée",                  cls: "bg-[#FEE2E2] text-[#B91C1C]" },
};

/**
 * Unified card for Trigo Express courses.
 * role="driver" | "passenger" — adapts the visible actions and info accordingly.
 */
export default function ExpressCourseCard({ b, role, onUpdate }) {
  const [otpInput, setOtpInput] = useState("");
  const [otpError, setOtpError] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showPickupMap, setShowPickupMap] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  // Iter 76 — female voice announces the final amount and reminds
  // driver + passenger to check for forgotten personal effects. We
  // remember the last-announced amount per booking so the audio only
  // plays once when the trip is finished, even if the parent re-renders.
  const spokenRef = useRef(null);

  const isDriver = role === "driver";
  // Passenger can cancel an Express ride as long as the trip has not yet
  // begun (OTP still not verified) and it's not already terminated.
  const canPassengerCancel = !isDriver && !b.otp_verified && !["completed", "cancelled"].includes(b.status);
  const status = STATUS_LABEL[b.status] || STATUS_LABEL.pending;
  const paymentConfirmed = !!b.payment_confirmed_by_driver;

  // Play the TTS announcement (fire-and-forget, no toast on failure).
  const playFinishAudio = (amount) => {
    try {
      if (!amount || spokenRef.current === `${b.id}:${amount}`) return;
      spokenRef.current = `${b.id}:${amount}`;
      const base = (api.defaults?.baseURL || "").replace(/\/$/, "");
      const url = `${base}/tts/finish-trip.mp3?amount=${encodeURIComponent(amount)}`;
      const audio = new Audio(url);
      audio.preload = "auto";
      audio.play().catch(() => { /* autoplay blocked or offline — silent fail */ });
    } catch { /* silent */ }
  };

  // Trigger the announcement as soon as the booking flips to "trip
  // finished" (works for both driver click and passenger WS update).
  useEffect(() => {
    if (b.trip_finished_at && !paymentConfirmed) {
      playFinishAudio(b.total_amount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.trip_finished_at, b.total_amount, paymentConfirmed]);

  // Passenger side — the WebSocket `trip_finished` event fires before the
  // parent re-fetches the bookings list, so listen for it directly to
  // play the voice cue as soon as the driver taps "Terminer la course".
  useEffect(() => {
    const onWs = (ev) => {
      const msg = ev?.detail;
      if (!msg || msg.type !== "trip_finished" || msg.booking_id !== b.id) return;
      const amount = msg.voice_amount || msg.total_amount || b.total_amount;
      if (amount) playFinishAudio(amount);
    };
    window.addEventListener("trigo:ws-message", onWs);
    return () => window.removeEventListener("trigo:ws-message", onWs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.id, b.total_amount]);

  const departureCoords = useMemo(() => (b.pickup_lat && b.pickup_lng ? { lat: b.pickup_lat, lng: b.pickup_lng } : null), [b.pickup_lat, b.pickup_lng]);
  const destinationCoords = useMemo(() => (b.dropoff_lat && b.dropoff_lng ? { lat: b.dropoff_lat, lng: b.dropoff_lng } : null), [b.dropoff_lat, b.dropoff_lng]);
  const driverLocation = useMemo(() => (b.driver_lat && b.driver_lng ? { lat: b.driver_lat, lng: b.driver_lng } : null), [b.driver_lat, b.driver_lng]);
  const showMap = b.status === "confirmed" || b.status === "in_progress";
  const canReview = b.status === "completed" && !b.has_reviewed;

  // Auto-accept dialog is not automatic — the driver clicks the button.
  const accept = async () => {
    setLoading(true);
    try {
      await api.post(`/express/${b.id}/accept`);
      // Immediately start GPS tracking so the passenger sees the driver approach.
      if (isDriver) {
        window.dispatchEvent(new CustomEvent("trigo:start-tracking", { detail: { bookingId: b.id } }));
        // Notify the global listener so the driver lands on the Courses
        // Express tab automatically, even if they accepted from another view.
        window.dispatchEvent(new CustomEvent("trigo:express-accepted", { detail: { bookingId: b.id } }));
      }
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  const refuse = async () => {
    setLoading(true);
    try {
      await api.post(`/express/${b.id}/decline`);
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  const verifyOtp = async () => {
    if (otpInput.length !== 4) { setOtpError("Code à 4 chiffres requis"); return; }
    setLoading(true);
    try {
      await api.post(`/bookings/${b.id}/verify-otp`, { otp: otpInput });
      setOtpInput(""); setOtpError("");
      onUpdate();
    } catch (e) { setOtpError(e.response?.data?.detail || "Code incorrect"); }
    finally { setLoading(false); }
  };
  const confirmPayment = async () => {
    setConfirming(true);
    try {
      await api.post(`/express/${b.id}/confirm-payment`);
      toast.success("Paiement confirmé ✓");
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setConfirming(false); }
  };
  const completeTrip = async () => {
    setCompleting(true);
    try {
      await api.post(`/bookings/${b.id}/complete`);
      toast.success("Course terminée");
      // Auto-open review modal so both parties can immediately rate each other
      setShowReview(true);
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setCompleting(false); }
  };

  // Iter 75 — Driver clicks "Terminer la course" BEFORE payment. Freezes
  // the chrono server-side, locks any overtime fee, and reveals the
  // "Montant reçu conforme" button with the FINAL amount.
  const finishTrip = async () => {
    setFinishing(true);
    try {
      const { data } = await api.post(`/bookings/${b.id}/finish-trip`);
      toast.success("Chrono arrêté", { description: "Vous pouvez maintenant encaisser le paiement." });
      // Kick off the female-voice announcement immediately so the
      // driver hears the amount and the "check for forgotten items"
      // reminder before the parent re-fetches the bookings list.
      const amount = data?.voice_amount || data?.total_amount || b.total_amount;
      if (amount) playFinishAudio(amount);
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setFinishing(false); }
  };

  const [extending, setExtending] = useState(false);
  const extendForfait = async () => {
    if (!window.confirm("Ajouter 15 minutes au forfait ? Le tarif de dépassement sera appliqué automatiquement.")) return;
    setExtending(true);
    try {
      const { data } = await api.post(`/express/${b.id}/extend`);
      toast.success(`+15 min ajoutées (+${(data.added_fee || 0).toLocaleString("fr-FR")} F)`, {
        description: `Nouveau total : ${(data.total_amount || 0).toLocaleString("fr-FR")} F`,
      });
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setExtending(false); }
  };

  // Live tracking share (WhatsApp) — mirrors the flow used by hourly rentals.
  // The passenger can share the trip live with a relative once the OTP has
  // been verified so the trip is truly ongoing (backend endpoint already
  // supports Express bookings since they are stored in the same collection).
  const handleShareLive = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const { data } = await api.post(`/bookings/${b.id}/share-token`);
      const trackUrl = `${window.location.origin}/track/${data.token}`;
      const text = [
        `📍 Suivez ma course Trigo Express en direct :`,
        `${trackUrl}`,
        ``,
        `Chauffeur : ${b.driver_name || "N/A"}${b.vehicle_plate ? ` (${b.vehicle_plate})` : ""}`,
        b.pickup_address && b.dropoff_address ? `Trajet : ${b.pickup_address} → ${b.dropoff_address}` : "",
        `Vous pourrez suivre le véhicule sur la carte en temps réel.`,
      ].filter(Boolean).join("\n");
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de générer le lien de suivi");
    } finally { setSharing(false); }
  };

  // Contact info: passenger sees driver, driver sees passenger
  const contactName = isDriver ? b.user_name : b.driver_name;
  const contactPhone = isDriver ? b.user_phone : b.driver_phone;
  const contactAvatar = isDriver ? "" : resolveImageUrl(b.driver_avatar || "");

  return (
    <div className="bg-white rounded-3xl border border-[#E2E8F0] p-5 shadow-sm" data-testid={`express-card-${b.id}`} style={{ fontFamily: "Outfit, Inter, sans-serif" }}>
      {/* Top row: express badge + status + amount */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FACC15] text-[#0A2540] text-[10px] font-extrabold uppercase tracking-widest" style={{ fontFamily: "Outfit" }}>
          <Zap className="w-3 h-3" /> Course Express
        </span>
        <span className={`text-[10.5px] font-bold px-2.5 py-1 rounded-full ${status.cls}`}>{status.label}</span>
        <span className="ml-auto text-lg font-extrabold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>{fmtXAF(b.total_amount)}</span>
      </div>

      {/* Completed recap — total charged, breakdown for overtime (driver-focused) */}
      {b.status === "completed" && isDriver && (
        <div className="mb-3 p-3 rounded-2xl bg-[#DCFCE7] border border-[#10B981]/40" data-testid={`express-completed-total-${b.id}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-[#065F46] flex items-center gap-1">
              ✓ Course terminée · total encaissé
            </span>
            <span className="text-xl font-extrabold text-[#065F46]" style={{ fontFamily: "Outfit" }}>
              {fmtXAF(b.total_amount)}
            </span>
          </div>
          {(b.extra_minutes || 0) > 0 && (
            <div className="mt-1.5 text-[11px] text-[#065F46] flex justify-between border-t border-[#10B981]/30 pt-1.5">
              <span>Base : {fmtXAF(b.original_forfait_price || 0)}</span>
              <span>+ Dépassement : +{b.extra_minutes} min · +{fmtXAF(b.overtime_fee || 0)}</span>
            </div>
          )}
        </div>
      )}

      {/* Contact block */}
      <div className="flex items-center gap-3 rounded-2xl p-3 mb-3 bg-[#F5F7FA]" data-testid={`express-contact-${b.id}`}>
        {contactAvatar ? (
          <img src={contactAvatar} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-[#FACC15]" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-[#0A2540] flex items-center justify-center text-[#FACC15] text-lg font-extrabold" style={{ fontFamily: "Outfit" }}>
            {(contactName || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-extrabold text-[#0A2540] truncate" style={{ fontFamily: "Outfit" }}>
            {contactName || (isDriver ? "Passager" : "Chauffeur")}
          </div>
          {!isDriver && b.driver_rating > 0 && (
            <div className="text-xs text-[#64748B] flex items-center gap-1">
              <Star className="w-3 h-3 text-[#FACC15]" fill="#FACC15" /> {b.driver_rating.toFixed(1)}
              {b.driver_eta_min ? ` · ETA ${b.driver_eta_min} min` : ""}
            </div>
          )}
          {contactPhone && (
            <div className="text-xs text-[#64748B]">{contactPhone}</div>
          )}
        </div>
        {contactPhone && (
          <div className="flex items-center gap-1.5 shrink-0">
            <a href={`tel:${contactPhone}`} className="w-10 h-10 rounded-full bg-[#0A2540] text-white flex items-center justify-center hover:bg-[#081b2e] transition" data-testid={`express-call-${b.id}`}>
              <Phone className="w-4 h-4" />
            </a>
            <a href={`https://wa.me/${contactPhone.replace(/[^0-9+]/g, "").replace(/^\+/, "")}`} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-[#25D366] text-white flex items-center justify-center hover:bg-green-600 transition" data-testid={`express-whatsapp-${b.id}`}>
              <MessageCircle className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>

      {/* Vehicle block (passenger-only) */}
      {!isDriver && b.vehicle_info && (
        <div className="flex items-center gap-3 rounded-2xl p-3 mb-3 bg-[#0A2540] text-white" data-testid={`express-vehicle-${b.id}`}>
          {b.vehicle_image_url ? (
            <img src={resolveImageUrl(b.vehicle_image_url)} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-[#FACC15]" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-[#FACC15] flex items-center justify-center text-xl">
              {b.express_vehicle_emoji || "🚗"}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-extrabold truncate" style={{ fontFamily: "Outfit" }}>
              {b.vehicle_info}{b.vehicle_color ? ` · ${b.vehicle_color}` : ""}
            </div>
            <div className="text-xs text-white/70">
              Plaque : <span className="font-mono font-bold text-[#FACC15]">{b.vehicle_plate || "—"}</span>
              {b.express_vehicle_label ? ` · ${b.express_vehicle_label}` : ""}
            </div>
          </div>
        </div>
      )}

      {/* Trip addresses */}
      <div className="space-y-2 mb-3 text-sm">
        <div className="flex items-start gap-2">
          <span className="w-5 h-5 rounded-full bg-[#0A2540] text-white text-[10px] font-extrabold flex items-center justify-center mt-0.5 shrink-0">A</span>
          <div
            className={`flex-1 min-w-0 ${isDriver && b.pickup_lat && b.pickup_lng ? "cursor-pointer" : ""}`}
            onClick={isDriver && b.pickup_lat && b.pickup_lng ? () => setShowPickupMap(true) : undefined}
            role={isDriver && b.pickup_lat && b.pickup_lng ? "button" : undefined}
          >
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#64748B]">Départ</span>
            <p className="text-[#0A2540] leading-tight break-words font-medium hover:underline">{b.pickup_address}</p>
            {isDriver && b.pickup_lat && b.pickup_lng ? (
              <div className="mt-1.5 flex gap-1.5 flex-wrap" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  onClick={() => setShowPickupMap(true)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FACC15] text-[#0A2540] text-[10.5px] font-bold hover:bg-[#FACC15]/80 transition"
                  data-testid={`express-view-pickup-map-${b.id}`}
                >
                  <MapIcon className="w-3 h-3" /> Voir sur la carte
                </button>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${b.pickup_lat},${b.pickup_lng}&travelmode=driving`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#0A2540] text-white text-[10.5px] font-semibold hover:bg-[#0A2540]/90 transition"
                  data-testid={`express-navigate-pickup-gmaps-${b.id}`}
                >
                  <Navigation className="w-3 h-3" /> Naviguer
                </a>
                <a
                  href={`https://waze.com/ul?ll=${b.pickup_lat},${b.pickup_lng}&navigate=yes`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#33CCFF] text-white text-[10.5px] font-semibold hover:bg-[#33CCFF]/90 transition"
                  data-testid={`express-navigate-pickup-waze-${b.id}`}
                >
                  Waze
                </a>
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-5 h-5 rounded-full bg-[#FACC15] text-[#0A2540] text-[10px] font-extrabold flex items-center justify-center mt-0.5 shrink-0">B</span>
          <div className="flex-1 min-w-0">
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#64748B]">Destination</span>
            <p className="text-[#0A2540] leading-tight break-words font-medium">{b.dropoff_address}</p>
            {isDriver && b.dropoff_lat && b.dropoff_lng ? (
              <div className="mt-1.5 flex gap-1.5 flex-wrap">
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${b.dropoff_lat},${b.dropoff_lng}&travelmode=driving`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#10B981] text-white text-[10.5px] font-semibold hover:bg-[#10B981]/90 transition"
                  data-testid={`express-navigate-dropoff-gmaps-${b.id}`}
                >
                  <Navigation className="w-3 h-3" /> Naviguer
                </a>
                <a
                  href={`https://waze.com/ul?ll=${b.dropoff_lat},${b.dropoff_lng}&navigate=yes`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#33CCFF] text-white text-[10.5px] font-semibold hover:bg-[#33CCFF]/90 transition"
                  data-testid={`express-navigate-dropoff-waze-${b.id}`}
                >
                  Waze
                </a>
              </div>
            ) : null}
          </div>
        </div>
        {b.estimated_distance_km && (
          <div className="text-[11px] text-[#64748B] pt-1">
            <Navigation className="w-3 h-3 inline mr-1" /> ~{b.estimated_distance_km.toFixed(1)} km · {Math.round(b.estimated_duration_min || 0)} min
          </div>
        )}
      </div>

      {/* Big OTP block (PASSENGER only) — shown BEFORE the map so the code
          is impossible to miss. Displayed while the driver is en route and
          the code hasn't been verified yet. */}
      {!isDriver && b.status === "confirmed" && b.otp_code && !b.otp_verified && (
        <div
          className="mb-3 rounded-3xl border-2 border-[#FACC15] bg-gradient-to-br from-[#FEF9C3] to-[#FEF3C7] px-4 py-4 sm:px-6 sm:py-5 shadow-sm"
          data-testid={`express-otp-big-${b.id}`}
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-[#0A2540] flex items-center justify-center shrink-0">
              <KeyRound className="w-4 h-4 text-[#FACC15]" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest font-extrabold text-[#854D0E]">
                Code à donner au chauffeur
              </p>
              <p className="text-[11px] text-[#7A5B0F] leading-tight">
                Communiquez ce code uniquement une fois à bord du véhicule.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-center py-1">
            <span
              className="font-mono font-black text-[#0A2540] text-4xl sm:text-5xl md:text-6xl tracking-[0.25em] sm:tracking-[0.35em] select-all"
              style={{ fontFamily: "Outfit, ui-monospace, monospace" }}
              data-testid={`express-otp-code-${b.id}`}
            >
              {b.otp_code}
            </span>
          </div>
        </div>
      )}

      {/* Driver OTP verification block — placed BEFORE the map so the input
          field is always within easy reach and the driver never has to scroll
          past the map to enter the passenger's 4-digit code. */}
      {isDriver && b.status === "confirmed" && !b.otp_verified && (
        <div
          className="mb-3 rounded-3xl border-2 border-[#0F2B46] bg-gradient-to-br from-[#F5F7FA] to-white px-4 py-4 sm:px-5 shadow-sm"
          data-testid={`express-otp-driver-block-${b.id}`}
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-[#FACC15] flex items-center justify-center shrink-0">
              <KeyRound className="w-4 h-4 text-[#0F2B46]" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-widest font-extrabold text-[#0F2B46]">
                Démarrer la course
              </p>
              <p className="text-[11px] text-[#5B6B7A] leading-tight">
                Demandez au passager son code à 4 chiffres pour commencer.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <Input
              value={otpInput}
              onChange={(e) => { setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 4)); setOtpError(""); }}
              placeholder="Code OTP (4 chiffres)"
              inputMode="numeric"
              className="h-12 text-center font-mono font-extrabold text-2xl tracking-[0.35em] flex-1 border-2 border-[#0F2B46]/20 focus-visible:border-[#FACC15] focus-visible:ring-[#FACC15]"
              data-testid={`express-otp-input-${b.id}`}
            />
            <Button
              onClick={verifyOtp}
              disabled={loading || otpInput.length !== 4}
              className="h-12 px-6 bg-[#0F2B46] text-white hover:bg-[#0F2B46]/90 font-bold text-sm"
              data-testid={`express-otp-verify-${b.id}`}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Vérifier & Démarrer"}
            </Button>
          </div>
          {otpError && (
            <p className="mt-2 text-xs text-[#C1453B] font-semibold" data-testid={`express-otp-error-${b.id}`}>
              {otpError}
            </p>
          )}
        </div>
      )}

      {/* Live map: from confirmed to in_progress */}
      {showMap && (
        <div className="mb-3" data-testid={`express-map-${b.id}`}>
          <div className="text-[10.5px] font-bold uppercase tracking-widest text-[#5B6B7A] mb-1 flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {b.status === "confirmed" ? "Chauffeur en approche…" : "Trajet en cours"}
          </div>
          <LiveTrackingMap
            bookingId={b.id}
            driverLocation={driverLocation}
            departure={b.pickup_address}
            destination={b.dropoff_address}
            departureCoords={b.status === "confirmed" ? departureCoords : departureCoords}
            destinationCoords={b.status === "in_progress" ? destinationCoords : departureCoords}
            height="260px"
          />
        </div>
      )}

      {/* Chrono once trip has started */}
      <TripChronometer booking={{ ...b, rental_mode: "hourly" }} compact role={role} />

      {/* Actions row */}
      <div className="flex flex-wrap items-center gap-2 mt-3">
        {isDriver && b.status === "pending" && (
          <>
            <Button size="sm" onClick={accept} disabled={loading} className="bg-[#3FAE7A] text-white hover:bg-emerald-700" data-testid={`express-accept-${b.id}`}>
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />} Accepter
            </Button>
            <Button size="sm" variant="outline" onClick={refuse} disabled={loading} className="border-[#C1453B] text-[#C1453B]" data-testid={`express-refuse-${b.id}`}>
              <XCircle className="w-3.5 h-3.5 mr-1" /> Refuser
            </Button>
          </>
        )}
        {/* Note: legacy "Annuler la course" (pending only, no reason picker)
            has been removed. The passenger now uses the shared
            `CancelBookingDialog` variant declared below in the action row
            so the cancellation motive is always documented. */}

        {/* Note: the driver OTP verification block has been moved ABOVE the
            live map to keep it always within thumb's reach. */}

        {/* OTP display (passenger) — small inline reminder (the big block is
            already displayed above, before the map). Kept here so the code
            stays visible next to the actions row on tall cards. */}
        {!isDriver && b.status === "confirmed" && b.otp_code && !b.otp_verified && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F2A93B]/15 rounded-lg" data-testid={`express-otp-display-${b.id}`}>
            <KeyRound className="w-4 h-4 text-[#D98C1F]" />
            <span className="text-sm font-mono font-extrabold text-[#0F2B46] tracking-widest">{b.otp_code}</span>
            <span className="text-[10px] text-[#5B6B7A]">à donner au chauffeur</span>
          </div>
        )}

        {/* Live tracking share (passenger) — visible as soon as the OTP has
            been verified and the trip is still active. Mirrors the flow used
            for hourly rentals. */}
        {!isDriver && b.otp_verified && b.status !== "completed" && b.status !== "cancelled" && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleShareLive}
            disabled={sharing}
            className="border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10"
            data-testid={`express-share-tracking-${b.id}`}
          >
            {sharing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Share2 className="w-3.5 h-3.5 mr-1" />}
            {sharing ? "Génération…" : "Partager le suivi"}
          </Button>
        )}

        {/* Forfait active — driver can extend +15 min */}
        {b.status === "in_progress" && b.pricing_mode === "forfait" && isDriver && (
          <div className="w-full flex items-center gap-2 flex-wrap p-2.5 rounded-lg bg-[#FEF9C3] border border-[#FACC15]/40" data-testid={`express-forfait-info-${b.id}`}>
            <span className="text-[11px] font-bold text-[#854D0E]">
              ⏱️ Forfait {b.forfait_label} ({b.forfait_duration_min} min)
              {b.extra_minutes > 0 && ` · +${b.extra_minutes} min supplémentaires`}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={extendForfait}
              disabled={extending}
              className="ml-auto h-7 px-2.5 text-[11px] border-[#FACC15] text-[#854D0E] hover:bg-[#FACC15]/20"
              data-testid={`express-extend-${b.id}`}
            >
              {extending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : "+"}
              15 min
            </Button>
          </div>
        )}

        {/* Payment confirmation (driver, in_progress, before completion) */}
        {b.status === "in_progress" && (
          <div className="w-full">
            {/* Iter 75 — Step 0: driver stops the chronometer BEFORE cash.
                Only shows for the driver, before payment confirmation and
                before the chrono was already frozen. */}
            {isDriver && !b.trip_finished_at && !paymentConfirmed && (
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Button
                  size="sm"
                  onClick={finishTrip}
                  disabled={finishing}
                  className="bg-[#0A2540] text-white hover:bg-[#0F2B46] font-bold"
                  data-testid={`express-finish-trip-${b.id}`}
                >
                  {finishing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Flag className="w-3.5 h-3.5 mr-1" />}
                  Terminer la course
                </Button>
                <span className="text-[11px] text-[#5B6B7A]">Arrête le chrono avant l'encaissement.</span>
              </div>
            )}
            {/* Step A: driver must first confirm cash received (only after
                the chrono has been frozen). */}
            {isDriver && !!b.trip_finished_at && !paymentConfirmed && (
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={confirmPayment} disabled={confirming} className="bg-[#F2A93B] text-[#0F2B46] hover:bg-[#E09520] font-bold" data-testid={`express-confirm-payment-${b.id}`}>
                  {confirming ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Wallet className="w-3.5 h-3.5 mr-1" />}
                  Montant reçu conforme ({fmtXAF(b.total_amount)})
                </Button>
                <span className="text-[11px] text-[#5B6B7A]">Confirmez que vous avez reçu le paiement du passager.</span>
              </div>
            )}
            {/* Passenger side messaging — reflects the driver's current step. */}
            {!isDriver && !paymentConfirmed && !b.trip_finished_at && (
              <div className="flex items-center gap-2 px-3 py-2 bg-[#E0E7FF]/40 border border-[#0A2540]/20 rounded-lg" data-testid={`express-trip-in-progress-${b.id}`}>
                <Flag className="w-4 h-4 text-[#0A2540]" />
                <span className="text-xs text-[#0F2B46]">Le chauffeur clôturera la course une fois arrivé.</span>
              </div>
            )}
            {!isDriver && !paymentConfirmed && !!b.trip_finished_at && (
              <div className="flex items-center gap-2 px-3 py-2 bg-[#F2A93B]/10 border border-[#F2A93B]/30 rounded-lg" data-testid={`express-waiting-payment-${b.id}`}>
                <Wallet className="w-4 h-4 text-[#D98C1F]" />
                <span className="text-xs text-[#0F2B46]">Remettez <b>{fmtXAF(b.total_amount)}</b> au chauffeur. Il va confirmer la réception.</span>
              </div>
            )}
            {/* Step B: payment confirmed → clôture course */}
            {paymentConfirmed && (
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10.5px] font-bold" data-testid={`express-payment-badge-${b.id}`}>
                  <CheckCircle className="w-3 h-3" /> Paiement confirmé
                </div>
                {isDriver ? (
                  <Button size="sm" onClick={completeTrip} disabled={completing} className="bg-[#3FAE7A] text-white hover:bg-emerald-700" data-testid={`express-complete-${b.id}`}>
                    {completing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
                    Clore la course
                  </Button>
                ) : (
                  <Button size="sm" onClick={completeTrip} disabled={completing} className="bg-[#3FAE7A] text-white hover:bg-emerald-700" data-testid={`express-complete-passenger-${b.id}`}>
                    {completing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
                    Clore et noter la course
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Review after completion */}
        {canReview && (
          <Button size="sm" onClick={() => setShowReview(true)} className="bg-[#F2A93B] text-[#0F2B46] hover:bg-[#D98C1F]" data-testid={`express-review-${b.id}`}>
            <Star className="w-3.5 h-3.5 mr-1" /> Laisser un avis
          </Button>
        )}
        {b.has_review && <ReviewBadge rating={b.review_rating} />}

        {/* Passenger cancellation — mirrors the hourly-rental flow. Requires
            the trip to not have started yet (otp_verified === false) so the
            driver isn't left on-scene. Uses the shared `CancelBookingDialog`
            which forces a documented reason (Voyage annulé / Chauffeur
            inaccessible / Annulation à la demande du chauffeur / Autres). */}
        {canPassengerCancel && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowCancel(true)}
            className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white"
            data-testid={`express-cancel-btn-${b.id}`}
          >
            <XCircle className="w-3.5 h-3.5 mr-1" /> Annuler la course
          </Button>
        )}
      </div>

      {/* Cancellation reason badge — shown once the ride is in the cancelled
          state so both parties (and the admin panel) always know why. */}
      {b.status === "cancelled" && b.cancellation_reason_label && (
        <div className="mt-3 flex items-start gap-2 text-xs text-[#EF4444] bg-[#EF4444]/5 border border-[#EF4444]/20 rounded-lg px-3 py-2" data-testid={`express-cancel-reason-${b.id}`}>
          <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <div>
            <span className="font-bold">Motif : </span>{b.cancellation_reason_label}
            {b.cancellation_reason_details && (
              <span className="block mt-0.5 text-[#7F1D1D] italic">« {b.cancellation_reason_details} »</span>
            )}
          </div>
        </div>
      )}

      {showReview && (
        <ReviewModal
          open={showReview}
          bookingId={b.id}
          targetUserId={isDriver ? b.user_id : b.driver_id}
          targetUserName={isDriver ? b.user_name : b.driver_name}
          onClose={() => setShowReview(false)}
          onReviewed={() => { setShowReview(false); onUpdate(); }}
        />
      )}
      {isDriver && (
        <DriverPickupMapDialog
          open={showPickupMap}
          onOpenChange={setShowPickupMap}
          pickup={b.pickup_lat && b.pickup_lng ? { lat: b.pickup_lat, lng: b.pickup_lng, address: b.pickup_address } : null}
          passengerName={b.user_name}
          bookingLabel={b.pickup_address}
          bookingId={b.id}
        />
      )}
      <CancelBookingDialog
        open={showCancel}
        onClose={() => setShowCancel(false)}
        bookingId={b.id}
        onCancelled={() => { setShowCancel(false); onUpdate(); }}
      />
    </div>
  );
}
